/**
 * DAT Loadboard (epic ATL-001416) — React Query hooks.
 *
 * Every hook is disabled unless the org has the `dat_loadboard` enterprise
 * feature; the server also answers 403 without it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DatLaneBenchmark, DatMarginCustomAttribute, DatRoleAccess } from '@tracxtms/shared';
import api from '../lib/api';
import { dispatchLoadsEditUrl, resolveVariantKey } from '../pages/dispatch-loads/dispatch-loads-route';
import { useFeature, useFeaturePreference } from './use-enterprise-features';
import { useAuthStore } from '../stores/auth-store';
import { usePermissionsStore } from '../stores/permissions-store';

// ─── Types ───────────────────────────────────────────────────────────────────

export type DatEnvironment = 'staging' | 'production' | 'mock';

export interface DatConnectionStatus {
  configured: boolean;
  isActive: boolean;
  environment: DatEnvironment | null;
  lastTestAt: string | null;
  lastTestResult: string | null;
}

/** A1 (ATL-001419): one org user and their DAT login. */
export interface DatUserRow {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  datUsername: string | null;
  suggestedDatUsername: string;
  isActive: boolean;
  lastVerifiedAt: string | null;
  lastVerifyResult: 'success' | 'failure' | null;
  lastVerifyError: string | null;
}

export interface DatVerifyResult {
  userId: string;
  success: boolean;
  errorCode: string | null;
  message: string | null;
  /** e.g. `retryAt` while DAT requests are paused (A7). */
  details?: Record<string, unknown>;
}

/** A4 (ATL-001425): one limit window (hour or month). */
export interface DatQuotaWindow {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string;
}

export interface DatQuotaUsage {
  search: { hour: DatQuotaWindow; month: DatQuotaWindow };
  post: { month: DatQuotaWindow };
  rateLimitedUntil: { search: string | null; post: string | null };
}

/** GET /integrations/dat/me — the signed-in user's DAT state. */
export interface DatMe {
  configured: boolean;
  linked: boolean;
  datUsername: string | null;
  quota: DatQuotaUsage | null;
  /** What the org's Type of Business offers on DAT (server-resolved). */
  roleAccess: DatRoleAccess;
}

export interface DatAutoLinkResult {
  linked: number;
  skipped: Array<{ userId: string; reason: 'DAT_LOGIN_TAKEN' }>;
}

// ─── Keys ────────────────────────────────────────────────────────────────────

export const datKeys = {
  status: ['dat', 'status'] as const,
  users: ['dat', 'users'] as const,
  me: ['dat', 'me'] as const,
  equipmentCatalog: ['dat', 'equipment', 'catalog'] as const,
  equipmentMappings: ['dat', 'equipment', 'mappings'] as const,
  posting: (loadId: string) => ['dat', 'posting', loadId] as const,
  /** C6 badge; the prefix `['dat', 'postingStatus', loadId]` also covers the grid row. */
  postingStatus: (loadId: string) => ['dat', 'postingStatus', loadId] as const,
  postingRow: (loadId: string) => ['dat', 'postingStatus', loadId, 'row'] as const,
  loadDetail: (resultId: string) => ['dat', 'load-detail', resultId] as const,
  monitoring: (failuresOnly: boolean) => ['dat', 'monitoring', failuresOnly] as const,
  savedSearches: ['dat', 'saved-searches'] as const,
};

// ─── Connection ──────────────────────────────────────────────────────────────

/** Pass `enabled: false` to skip the call (e.g. the user can't manage integrations). */
export function useDatStatus(options: { enabled?: boolean } = {}) {
  const featureOn = useFeature('dat_loadboard');
  return useQuery({
    queryKey: datKeys.status,
    queryFn: async () => (await api.get<{ data: DatConnectionStatus }>('/integrations/dat/status')).data.data,
    enabled: featureOn && options.enabled !== false,
    staleTime: 60_000,
  });
}

/**
 * Whether the DAT tab shows on General Settings: the `dat_loadboard`
 * feature is on, the user can manage integrations, and the org has an
 * active DAT integration.
 *
 * I1 (ATL-001430): a failed Test Connection no longer hides the tab. A2
 * marks a runtime auth error by failing `lastTestResult`, and support must
 * be able to diagnose exactly that from the Activity section — hiding the
 * tab at that moment would defeat it. The connection banner and the
 * Activity health strip carry the failure state instead.
 */
export function useDatSettingsTabVisible(): boolean {
  const featureOn = useFeature('dat_loadboard');
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'super_admin');
  const canManage = usePermissionsStore((s) => s.can('integrations', 'manage'));
  const allowed = featureOn && (isSuperAdmin || canManage);
  const { data: status } = useDatStatus({ enabled: allowed });
  return allowed && !!status?.configured && status.isActive;
}

/**
 * The signed-in user's link status and remaining DAT searches/posts (A4).
 * Search/post screens invalidate `datKeys.me` after each DAT call.
 */
export function useDatMe() {
  const enabled = useFeature('dat_loadboard');
  return useQuery({
    queryKey: datKeys.me,
    queryFn: async () => (await api.get<{ data: DatMe }>('/integrations/dat/me')).data.data,
    enabled,
    staleTime: 15_000,
  });
}

/**
 * What the org's Type of Business offers on DAT (Carrier searches, Broker and
 * Shipper post; see `resolveDatRoleAccess` in @tracxtms/shared). Null until
 * `/dat/me` answers — callers treat that as "allowed" so the common case does
 * not flicker; the server refuses with DAT_ROLE_NOT_ALLOWED regardless.
 */
export function useDatRoleAccess(): DatRoleAccess | null {
  const { data } = useDatMe();
  return data?.roleAccess ?? null;
}

// ─── DAT Users mapping (A1) ──────────────────────────────────────────────────

export function useDatUsers() {
  const enabled = useFeature('dat_loadboard');
  return useQuery({
    queryKey: datKeys.users,
    queryFn: async () => (await api.get<{ data: DatUserRow[] }>('/integrations/dat/identities')).data.data,
    enabled,
  });
}

export function useLinkDatUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, datUsername }: { userId: string; datUsername: string }) =>
      (await api.put<{ data: DatUserRow }>(`/integrations/dat/identities/${userId}`, { datUsername })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: datKeys.users }),
  });
}

export function useUnlinkDatUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/integrations/dat/identities/${userId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: datKeys.users }),
  });
}

export function useAutoLinkDatUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ data: DatAutoLinkResult }>('/integrations/dat/identities/auto-link')).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: datKeys.users }),
  });
}

/** Verify all linked users, or only `userIds`. */
export function useVerifyDatUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userIds?: string[]) =>
      (await api.post<{ data: DatVerifyResult[] }>('/integrations/dat/identities/verify', userIds ? { userIds } : {})).data.data,
    onSettled: () => qc.invalidateQueries({ queryKey: datKeys.users }),
  });
}

// ── DAT – ATL-001421 (A5) Equipment mapping ─────────────────────────────────

export interface DatEquipmentTypeOption {
  code: string;
  name: string;
}

export interface DatEquipmentClassOption {
  code: string;
  name: string;
}

export interface DatEquipmentCatalog {
  types: DatEquipmentTypeOption[];
  classes: DatEquipmentClassOption[];
}

export interface DatEquipmentMappingRow {
  equipmentTypeConfigId: string;
  equipmentTypeName: string;
  datEquipmentType: string | null;
  datEquipmentName: string | null;
  seededDefault: boolean;
}

/** DAT's static Search v4 equipment catalog (80 types + 12 classes). */
export function useDatEquipmentCatalog() {
  const enabled = useFeature('dat_loadboard');
  return useQuery({
    queryKey: datKeys.equipmentCatalog,
    queryFn: async () =>
      (await api.get<{ data: DatEquipmentCatalog }>('/integrations/dat/equipment/catalog')).data.data,
    // The catalog is static for the life of the session.
    staleTime: Infinity,
    enabled,
  });
}

/**
 * Whether Settings → Equipment Types shows the DAT equipment mapping (column
 * + form field): the `dat_loadboard` feature is on and the org's DAT
 * integration is configured and active. `/me` reports `configured` only for
 * an active integration and needs just `loads:view`.
 */
export function useDatEquipmentMappingVisible(): boolean {
  const featureOn = useFeature('dat_loadboard');
  const { data: me } = useDatMe();
  return featureOn && !!me?.configured;
}

/** The org's Waypoint equipment type → DAT code rows. */
export function useDatEquipmentMappings(options: { enabled?: boolean } = {}) {
  const featureOn = useFeature('dat_loadboard');
  return useQuery({
    queryKey: datKeys.equipmentMappings,
    queryFn: async () =>
      (await api.get<{ data: DatEquipmentMappingRow[] }>('/integrations/dat/equipment/mappings')).data.data,
    enabled: featureOn && options.enabled !== false,
  });
}

/** Set (code) or clear (null) one mapping row. */
export function useSetDatEquipmentMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { equipmentTypeConfigId: string; datEquipmentType: string | null }) =>
      (
        await api.put<{ data: DatEquipmentMappingRow }>(
          `/integrations/dat/equipment/mappings/${input.equipmentTypeConfigId}`,
          { datEquipmentType: input.datEquipmentType },
        )
      ).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: datKeys.equipmentMappings }),
  });
}

// ─── Find Loads search (B1 / ATL-001428) ─────────────────────────────────────

export type DatSearchSort =
  | 'desc(age)'
  | 'asc(originDeadhead)'
  | 'asc(availability)'
  | 'desc(offerRate)'
  | 'desc(estimatedRatePerMile)'
  | 'asc(tripLength)';

export type DatSearchLocationInput =
  | { type: 'place'; locationId?: string; city?: string; state?: string; postalCode?: string; radiusMiles?: number }
  | { type: 'area'; zones?: string[]; states?: string[] }
  | { type: 'anywhere' };

export interface DatSearchCriteria {
  origin: DatSearchLocationInput;
  destination: DatSearchLocationInput;
  equipmentTypes?: string[];
  equipmentClasses?: string[];
  pickupFrom?: string;
  pickupTo?: string;
  fullPartial?: 'BOTH' | 'FULL' | 'PARTIAL';
  maxLengthFeet?: number;
  maxWeightPounds?: number;
  maxAgeMinutes?: number;
  orderBy?: DatSearchSort;
  limit?: number;
  cursor?: string;
  refresh?: boolean;
}

/** One DAT listing. DAT data — shown as-is, never translated or stored. */
export interface DatLoadListing {
  resultId: string;
  postingId: string;
  isActive: boolean;
  origin: { city: string; stateProv: string; postalCode: string | null };
  destination: { city: string; stateProv: string; postalCode: string | null };
  pickupDate: string | null;
  pickupStartHours: string | null;
  pickupEndHours: string | null;
  pickupAppointmentType: 'APPOINTMENT_REQUIRED' | 'FCFS' | 'TIME_WINDOW' | null;
  dropoffDate: string | null;
  dropoffStartHours: string | null;
  dropoffEndHours: string | null;
  dropoffAppointmentType: 'APPOINTMENT_REQUIRED' | 'FCFS' | 'TIME_WINDOW' | null;
  earliestWhen: string | null;
  latestWhen: string | null;
  equipmentType: string;
  equipmentName: string;
  reeferTemperature: number | null;
  fullPartial: string | null;
  lengthFeet: number | null;
  weightPounds: number | null;
  tripMiles: number | null;
  originDeadheadMiles: number | null;
  originDeadheadMethod: 'AIR' | 'ROAD' | null;
  destinationDeadheadMiles: number | null;
  destinationDeadheadMethod: 'AIR' | 'ROAD' | null;
  rateUsd: number | null;
  rateBasis: 'FLAT' | 'PER_MILE' | null;
  estimatedRatePerMile: number | null;
  companyName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  creditScore: number | null;
  daysToPay: number | null;
  brokerMcNumber: number | null;
  brokerDotNumber: number | null;
  hasTiaMembership: boolean | null;
  comments: string[];
  postedAt: string;
  expiresAt: string | null;
  isBookable: boolean;
  isNegotiable: boolean;
  isFactorable: boolean;
  isFromPrivateNetwork: boolean;
  isTrackingRequired: boolean;
  isRedacted: boolean;
  redactionReasons: string[];
  referenceId: string | null;
  /** B10 — serve-time stamps: already imported into Waypoint / expired. */
  inWaypoint?: DatImportedRef | null;
  isExpired?: boolean;
}

/** B10 — where an imported listing went (quote, and the load once accepted). */
export interface DatImportedRef {
  quoteId: string;
  quoteDisplayId: string;
  loadId: string | null;
  loadDisplayId: string | null;
  /** The load's module variant — picks the per-variant edit route. */
  loadModule: string | null;
}

export interface DatSearchPage {
  searchId: string;
  results: DatLoadListing[];
  totalCount: number;
  nextCursor: string | null;
  cached: boolean;
  searchedAt: string;
  warnings: Array<{ code: string; detail: string }>;
}

/** Whether the signed-in user may use Find Loads: feature + `search` preference + `loads:create` + Type of Business. */
export function useDatSearchEnabled(): boolean {
  const featureOn = useFeature('dat_loadboard');
  const searchPref = useFeaturePreference<boolean>('dat_loadboard', 'search', true);
  const canCreate = usePermissionsStore((s) => s.can('loads', 'create'));
  const roleAccess = useDatRoleAccess();
  return featureOn && searchPref !== false && canCreate && roleAccess?.searchLoads !== false;
}

/**
 * Run one page of a DAT search. A mutation, not a query: DAT searches only
 * run from a user's click (certification rule), never on mount or refetch.
 */
export function useDatLoadSearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (criteria: DatSearchCriteria) =>
      (await api.post<{ data: DatSearchPage }>('/integrations/dat/loads/search', criteria)).data.data,
    onSettled: () => qc.invalidateQueries({ queryKey: datKeys.me }),
  });
}

// ─── Find Loads saved filters ────────────────────────────────────────────────

/** One saved Find Loads filter set; `filters` is the page's form state. */
export interface DatSavedSearch {
  id: string;
  name: string;
  filters: Record<string, unknown>;
}

/** The signed-in user's saved Find Loads filters. Loading one never runs a search. */
export function useDatSavedSearches() {
  const enabled = useDatSearchEnabled();
  return useQuery({
    queryKey: datKeys.savedSearches,
    queryFn: async () => (await api.get<{ data: DatSavedSearch[] }>('/integrations/dat/saved-searches')).data.data,
    enabled,
    staleTime: 60_000,
  });
}

/** Replace the user's saved filter list (add / overwrite / delete happen client-side). */
export function useSaveDatSavedSearches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (searches: DatSavedSearch[]) =>
      (await api.put<{ data: DatSavedSearch[] }>('/integrations/dat/saved-searches', { searches })).data.data,
    onSuccess: (data) => qc.setQueryData(datKeys.savedSearches, data),
  });
}

// ─── Load detail drawer (B3 / ATL-001432) ────────────────────────────────────

/** The drawer payload: the listing plus DAT's `details` block + booking link. */
export interface DatLoadDetail {
  listing: DatLoadListing;
  commodity: string | null;
  pickupHours: string | null;
  dropOffHours: string | null;
  peerRating: { count: number; average: number } | null;
  bookingRedirectUrl: string | null;
  fetchedAt: string;
  cached: boolean;
}

/**
 * One DAT load's full detail. Fetches once per drawer open (the open IS the
 * user action; the server caches 5 minutes and spends one search quota unit
 * on a miss). All automatic refetching is off — DAT calls must never run on
 * focus/reconnect/retry timers (certification rule).
 */
export function useDatLoadDetail(resultId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: datKeys.loadDetail(resultId ?? 'none'),
    queryFn: async () => {
      const detail = (await api.get<{ data: DatLoadDetail }>(`/integrations/dat/loads/${encodeURIComponent(resultId!)}`)).data.data;
      if (!detail.cached) void qc.invalidateQueries({ queryKey: datKeys.me });
      return detail;
    },
    enabled: !!resultId,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

// ─── Post to DAT (C1 / ATL-001429) ───────────────────────────────────────────

export type DatPostingField =
  | 'origin'
  | 'destination'
  | 'equipmentType'
  | 'lengthFeet'
  | 'weightPounds'
  | 'earliestWhen'
  | 'latestWhen'
  | 'rateUsd'
  | 'comments';

export interface DatPostingIssue {
  field: DatPostingField;
  reason: 'required' | 'invalid' | 'past' | 'order' | 'tooLong' | 'unmapped' | string;
}

export interface DatPostingLaneEnd {
  locationName: string;
  city: string;
  stateProv: string;
  postalCode: string | null;
  latitude: number;
  longitude: number;
}

export interface DatPostingValues {
  equipmentType: string | null;
  lengthFeet: number | null;
  weightPounds: number | null;
  fullPartial: 'FULL' | 'PARTIAL';
  rateUsd: number | null;
  earliestWhen: string | null;
  latestWhen: string | null;
  comments: string[];
}

export interface DatPostingRow {
  id: string;
  loadId: string;
  datPostingId: string | null;
  status: string;
  postedAt: string;
  expiresAt: string | null;
  postedByUserId: string | null;
  postedByName: string | null;
  /** C6 badge state (server-derived). */
  state: DatPostingState;
  lastSyncedAt: string | null;
  lastRefreshedAt: string | null;
  /** C3: when Refresh on DAT is next allowed. */
  nextRefreshAt: string | null;
  removedAt: string | null;
  needsManualRemoval: boolean;
  /** An API error code (e.g. `DAT_UNAVAILABLE`) from the last failed update/refresh/remove. */
  lastError: string | null;
  lastErrorAt: string | null;
  /** Which action failed, so Retry repeats it. */
  lastErrorAction: 'update' | 'refresh' | 'remove' | null;
  /** C5: why the post must come off DAT (kept after removal as the reason). */
  coverReason: DatCoverReason | null;
  coveredAt: string | null;
  /** C5: failed automatic removals so far, and the next background retry (null = none due). */
  autoRemoveAttempts: number;
  autoRemoveNextAt: string | null;
}

/** C5: why a load no longer needs its DAT post. */
export type DatCoverReason = 'covered' | 'cancelled' | 'completed' | 'deleted';

/** C5: `datCover` on a load action's response, re-broadcast as the `dat:cover` window event. */
export interface DatCoverEvent {
  loadId: string;
  reason: DatCoverReason;
  mode: 'automatic' | 'prompt';
  /** False when the post was already flagged for this reason (no new toast). */
  fresh: boolean;
}

export type DatPostingState = 'posted' | 'refreshed' | 'expired' | 'removed' | 'error';

/** C6: GET /integrations/dat/postings?loadId= */
export interface DatPostingStatus {
  posting: DatPostingRow | null;
  /** C2: the load changed since the post was last sent to DAT. */
  outOfSync: boolean;
  updateMode: 'prompt' | 'automatic';
  /** C5: Automatic removes the post when the load is covered/closed; Prompt asks first. */
  autoRemoveMode: 'automatic' | 'prompt';
}

export interface DatPostingDraft {
  load: { id: string; displayId: string; status: string; moduleVariant: string };
  ineligibleReason: 'notBrokerage' | 'closed' | 'covered' | null;
  activePosting: DatPostingRow | null;
  outOfSync: boolean;
  origin: DatPostingLaneEnd | null;
  destination: DatPostingLaneEnd | null;
  values: DatPostingValues;
  equipmentName: string | null;
  issues: DatPostingIssue[];
  /** Margin Calculator: the load's customer rate and miles (suggests a max carrier offer). */
  pricing?: { customerRate: number | null; totalMiles: number | null };
}

export const DAT_POSTABLE_VARIANT = 'mod_ftl_brokerage_main';

/** Whether "Post to DAT" is offered: feature + `posting` preference (default off) + `loads:edit`. */
/**
 * What Waypoint creates from a DAT load: a load straight away (default) or a
 * tender first (`dat_loadboard` preference `import_mode`). The server decides
 * on send; this only labels the drawer button before the modal opens.
 */
export type DatImportMode = 'load' | 'tender';
export function useDatImportMode(): DatImportMode {
  const pref = useFeaturePreference<string>('dat_loadboard', 'import_mode', 'Load');
  return pref === 'Tender' ? 'tender' : 'load';
}

export function useDatPostingEnabled(): boolean {
  const featureOn = useFeature('dat_loadboard');
  const postingPref = useFeaturePreference<boolean>('dat_loadboard', 'posting', false);
  const canEdit = usePermissionsStore((s) => s.can('loads', 'edit'));
  return featureOn && postingPref === true && canEdit;
}

/**
 * Whether a NEW post may be made: posting allowed + the org's Type of Business
 * posts loads (not a Carrier). Existing posts stay manageable either way.
 */
export function useDatCanPostNew(): boolean {
  const enabled = useDatPostingEnabled();
  const roleAccess = useDatRoleAccess();
  return enabled && roleAccess?.postLoads !== false;
}

/** The pre-post modal's data (no DAT call, no quota). */
export function useDatPostingDraft(loadId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: datKeys.posting(loadId ?? ''),
    queryFn: async () =>
      (await api.get<{ data: DatPostingDraft }>(`/integrations/dat/loads/${loadId}/posting`)).data.data,
    enabled: !!loadId && enabled,
    staleTime: 0,
  });
}

export function usePostLoadToDat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { loadId: string; values: Partial<DatPostingValues> }) =>
      (await api.post<{ data: DatPostingRow }>(`/integrations/dat/loads/${input.loadId}/posting`, input.values)).data.data,
    onSettled: (_data, _err, input) => {
      qc.invalidateQueries({ queryKey: datKeys.me });
      qc.invalidateQueries({ queryKey: datKeys.posting(input.loadId) });
      qc.invalidateQueries({ queryKey: datKeys.postingStatus(input.loadId) });
    },
  });
}

// ─── Posting lifecycle: C2 update, C3 refresh, C4 remove, C6 status ─────────

/** Whether the DAT posting badge / grid column shows: feature + `posting` preference + `loads:view`. */
export function useDatPostingVisible(): boolean {
  const featureOn = useFeature('dat_loadboard');
  const postingPref = useFeaturePreference<boolean>('dat_loadboard', 'posting', false);
  const canView = usePermissionsStore((s) => s.can('loads', 'view'));
  return featureOn && postingPref === true && canView;
}

/** C6: one load's badge, with the out-of-sync check (form header, post modal). */
export function useDatPostingStatus(loadId: string | null | undefined, enabled = true) {
  const visible = useDatPostingVisible();
  return useQuery({
    queryKey: datKeys.postingStatus(loadId ?? ''),
    queryFn: async () =>
      (await api.get<{ data: DatPostingStatus }>('/integrations/dat/postings', { params: { loadId } })).data.data,
    enabled: visible && !!loadId && enabled,
    staleTime: 15_000,
  });
}

// The loads grid asks for many rows at once: collect the ids requested in the
// same tick and fetch them in one `?loadIds=` call (max 200 per call).
const ROW_BATCH_MAX = 200;
type RowWaiter = { resolve: (r: DatPostingRow | null) => void; reject: (e: unknown) => void };
let rowBatch = new Map<string, RowWaiter[]>();
let rowBatchTimer: ReturnType<typeof setTimeout> | null = null;

function flushRowBatch(): void {
  const batch = rowBatch;
  rowBatch = new Map();
  rowBatchTimer = null;
  const ids = [...batch.keys()];
  for (let i = 0; i < ids.length; i += ROW_BATCH_MAX) {
    const chunk = ids.slice(i, i + ROW_BATCH_MAX);
    api
      .get<{ data: Record<string, DatPostingRow | null> }>('/integrations/dat/postings', { params: { loadIds: chunk.join(',') } })
      .then((res) => chunk.forEach((id) => batch.get(id)?.forEach((w) => w.resolve(res.data.data[id] ?? null))))
      .catch((err) => chunk.forEach((id) => batch.get(id)?.forEach((w) => w.reject(err))));
  }
}

function fetchPostingRow(loadId: string): Promise<DatPostingRow | null> {
  return new Promise((resolve, reject) => {
    const waiters = rowBatch.get(loadId) ?? [];
    waiters.push({ resolve, reject });
    rowBatch.set(loadId, waiters);
    if (!rowBatchTimer) rowBatchTimer = setTimeout(flushRowBatch, 20);
  });
}

/** C6: one grid row's posting (batched with the other visible rows). */
export function useDatPostingRow(loadId: string | null | undefined, enabled = true) {
  const visible = useDatPostingVisible();
  return useQuery({
    queryKey: datKeys.postingRow(loadId ?? ''),
    queryFn: () => fetchPostingRow(loadId!),
    enabled: visible && !!loadId && enabled,
    staleTime: 30_000,
  });
}

export function useInvalidatePosting() {
  const qc = useQueryClient();
  return (loadId: string) => {
    qc.invalidateQueries({ queryKey: datKeys.postingStatus(loadId) });
    qc.invalidateQueries({ queryKey: datKeys.posting(loadId) });
    qc.invalidateQueries({ queryKey: datKeys.me });
  };
}

/** C2: push the load's changes (and the modal's edits) to its DAT post. `changed: false` = nothing to send. */
export function useUpdateDatPosting() {
  const invalidate = useInvalidatePosting();
  return useMutation({
    mutationFn: async (input: { loadId: string; values?: Partial<DatPostingValues> }) =>
      (await api.patch<{ data: { posting: DatPostingRow; changed: boolean } }>(
        `/integrations/dat/loads/${input.loadId}/posting`,
        input.values ?? {},
      )).data.data,
    onSettled: (_d, _e, input) => invalidate(input.loadId),
  });
}

/** C3: Refresh on DAT (once per 15 minutes; `DAT_REFRESH_TOO_SOON` otherwise). */
export function useRefreshDatPosting() {
  const invalidate = useInvalidatePosting();
  return useMutation({
    mutationFn: async (loadId: string) =>
      (await api.post<{ data: DatPostingRow }>(`/integrations/dat/loads/${loadId}/posting/refresh`)).data.data,
    onSettled: (_d, _e, loadId) => invalidate(loadId),
  });
}

/** C4: Remove from DAT. */
export function useRemoveDatPosting() {
  const invalidate = useInvalidatePosting();
  return useMutation({
    mutationFn: async (loadId: string) =>
      (await api.delete<{ data: DatPostingRow }>(`/integrations/dat/loads/${loadId}/posting`)).data.data,
    onSettled: (_d, _e, loadId) => invalidate(loadId),
  });
}

// ─── Monitoring / Activity (I1 / ATL-001430) ─────────────────────────────────

export interface DatMonitoring {
  health: {
    status: 'not_configured' | 'connected' | 'auth_error' | 'unavailable';
    environment: string | null;
    lastTestAt: string | null;
    lastTestResult: string | null;
    circuitOpenUntil: string | null;
  };
  quotas: Array<{
    userId: string;
    name: string;
    email: string;
    datUsername: string;
    lastVerifyResult: string | null;
    searchHourUsed: number;
    searchHourLimit: number;
    searchMonthUsed: number;
    searchMonthLimit: number;
    postMonthUsed: number;
    postMonthLimit: number;
    rateLimitedUntil: { search: string | null; post: string | null };
  }>;
  failedPostings: Array<{
    id: string;
    loadId: string;
    loadDisplayId: string | null;
    status: string;
    datPostingId: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    postedAt: string;
  }>;
  calls: Array<{
    at: string;
    user: string | null;
    eventType: string | null;
    method: string | null;
    path: string | null;
    statusCode: number | null;
    success: boolean;
    durationMs: number | null;
    errorMessage: string | null;
  }>;
}

/** Activity-tab read model. Reads Waypoint's own records — never calls DAT. */
export function useDatMonitoring(failuresOnly: boolean) {
  const featureOn = useFeature('dat_loadboard');
  return useQuery({
    queryKey: datKeys.monitoring(failuresOnly),
    queryFn: async () =>
      (await api.get<{ data: DatMonitoring }>(`/integrations/dat/monitoring${failuresOnly ? '?failuresOnly=true' : ''}`)).data.data,
    enabled: featureOn,
    staleTime: 15_000,
  });
}

/** I1 Retry on a failed posting: re-runs the C1 post with the load's defaults. */
export function useRetryDatPosting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (loadId: string) =>
      (await api.post<{ data: unknown }>(`/integrations/dat/loads/${loadId}/posting`, {})).data.data,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['dat', 'monitoring'] });
      void qc.invalidateQueries({ queryKey: datKeys.me });
    },
  });
}

// ─── Send to Tenders (B9 / ATL-001437) ───────────────────────────────────────

export interface DatBrokerCandidate {
  id: string;
  name: string;
  mcNumber: string | null;
  customerType: string;
  matchedBy: 'mc' | 'name';
}

export interface DatLocationMatchResult {
  location: { id: string; name: string; city: string | null; state: string | null; zip: string | null } | null;
  matchType: 'cityStateZip' | 'cityState' | null;
  proposal: { name: string; city: string; state: string; zip: string | null; country: string | null } | null;
}

export interface DatTenderPrep {
  detail: DatLoadDetail;
  /** What confirming the modal creates (org preference, read by the server). */
  importMode: DatImportMode;
  /** Modules the org's Type of Business allows the load in; first = default. */
  importModules: string[];
  /** B10 — set when this listing was already imported; the modal blocks. */
  existing: DatImportedRef | null;
  customer: {
    candidates: DatBrokerCandidate[];
    proposal: { name: string; mcNumber: string | null; dotNumber: string | null } | null;
  };
  origin: DatLocationMatchResult;
  destination: DatLocationMatchResult;
  equipment: {
    datCode: string;
    datName: string;
    waypoint: { id: string; name: string } | null;
    warning: string | null;
  };
}

export interface SendDatToTendersInput {
  customerId?: string | null;
  createCustomer?: boolean;
  originLocationId?: string | null;
  createOrigin?: boolean;
  destinationLocationId?: string | null;
  createDestination?: boolean;
  rateUsd?: number | null;
  module?: string;
}

/**
 * Modal data for Send to Tenders. A mutation, not a query: it re-fetches the
 * listing FRESH from DAT (one search quota unit), so it must only ever run
 * from the user's click — never on mount/focus timers.
 */
export function useDatTenderPrep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resultId: string) =>
      (await api.get<{ data: DatTenderPrep }>(`/integrations/dat/loads/${encodeURIComponent(resultId)}/tender-prep`)).data.data,
    onSettled: () => qc.invalidateQueries({ queryKey: datKeys.me }),
  });
}

/** Create the tender Quote from the listing (isTender, tenderSource 'dat'). */
/** Send result: the tender, plus the load in Load mode. */
export interface SendDatToTendersResult {
  mode: DatImportMode;
  quoteId: string;
  quoteDisplayId: string;
  loadId: string | null;
  loadDisplayId: string | null;
  loadModule: string | null;
  /** Load mode: the tender was created but converting it to a load failed. */
  loadFailed: boolean;
}

export function useSendDatToTender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ resultId, input }: { resultId: string; input: SendDatToTendersInput }) =>
      (await api.post<{ data: SendDatToTendersResult }>(
        `/integrations/dat/loads/${encodeURIComponent(resultId)}/tender`,
        input,
      )).data.data,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['quotes'] });
      void qc.invalidateQueries({ queryKey: ['loads'] });
      void qc.invalidateQueries({ queryKey: datKeys.me });
    },
  });
}

// ─── Errors ──────────────────────────────────────────────────────────────────

/** The server's error code (e.g. `DAT_LOGIN_TAKEN`), for `t('datLoadboard.errors.<code>')`. */
export function datErrorCode(err: unknown): string | null {
  const code = (err as { response?: { data?: { error?: { code?: unknown } } } })?.response?.data?.error?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * B10 — edit URL for an imported listing's Quote/Load. The load wins once
 * converted; its `moduleVariant` picks the per-variant route (a brokerage
 * tender's load does NOT live under `/dispatch-loads/freight/…`). Falls back
 * to the tender quote, the same target the Dispatch Tenders grid opens.
 */
export function datImportedRefUrl(ref: Pick<DatImportedRef, 'quoteId' | 'loadId' | 'loadModule'>): string {
  if (ref.loadId) {
    const variant = resolveVariantKey(ref.loadModule) ?? 'mod_dispatch_freight_main';
    return dispatchLoadsEditUrl(variant, ref.loadId);
  }
  return `/quotes/${ref.quoteId}/edit?from=tenders&tab=dat`;
}

// ─── Margin Calculator ───────────────────────────────────────────────────────

/** GET /integrations/dat/margin/settings — the org default and the user's own override + attributes. */
export interface DatMarginSettings {
  orgTargetMarginPct: number;
  userTargetMarginPct: number | null;
  targetMarginPct: number;
  attributes: DatMarginCustomAttribute[];
}

/** Board-offer spread on a lane (one DAT search) — not DAT RateView. */
export interface DatMarginBenchmarkResult {
  benchmark: DatLaneBenchmark | null;
  searchedAt: string;
  cached: boolean;
  /** 'state' when that end's city could not be located and the whole state was searched. */
  scope?: { origin: 'place' | 'state'; destination: 'place' | 'state' };
}

export interface DatMarginBenchmarkCriteria {
  origin: DatSearchLocationInput;
  destination: DatSearchLocationInput;
  equipmentTypes?: string[];
  refresh?: boolean;
}

const datMarginSettingsKey = ['dat', 'margin', 'settings'] as const;

/**
 * Whether the Margin Calculator is offered: feature + `margin_calculator`
 * preference (default ON) + `loads:view` + Type of Business (Broker, Mixed, Other).
 */
export function useDatMarginEnabled(): boolean {
  const featureOn = useFeature('dat_loadboard');
  const marginPref = useFeaturePreference<boolean>('dat_loadboard', 'margin_calculator', true);
  const canView = usePermissionsStore((s) => s.can('loads', 'view'));
  const roleAccess = useDatRoleAccess();
  return featureOn && marginPref !== false && canView && roleAccess?.priceLoads !== false;
}

/** The org default target margin from Feature Management (before any user override). */
export function useDatOrgTargetMargin(): number {
  const v = Number(useFeaturePreference<number | string>('dat_loadboard', 'default_target_margin_pct', 15));
  return Number.isFinite(v) && v >= 0 && v < 100 ? v : 15;
}

export function useDatMarginSettings() {
  const enabled = useDatMarginEnabled();
  return useQuery({
    queryKey: datMarginSettingsKey,
    queryFn: async () => (await api.get<{ data: DatMarginSettings }>('/integrations/dat/margin/settings')).data.data,
    enabled,
    staleTime: 60_000,
  });
}

/** Replace the user's target override (null = follow the org) and attribute library. */
export function useSaveDatMarginSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { targetMarginPct: number | null; attributes: DatMarginCustomAttribute[] }) =>
      (await api.put<{ data: DatMarginSettings }>('/integrations/dat/margin/settings', body)).data.data,
    onSuccess: (data) => qc.setQueryData(datMarginSettingsKey, data),
  });
}

/** Board-offer benchmark for a lane. A mutation: it runs one DAT search, so only on a click. */
export function useDatMarginBenchmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (criteria: DatMarginBenchmarkCriteria) =>
      (await api.post<{ data: DatMarginBenchmarkResult }>('/integrations/dat/margin/benchmark', criteria)).data.data,
    onSettled: () => qc.invalidateQueries({ queryKey: datKeys.me }),
  });
}
